import * as React from 'react';
import { Card, CardBody, Grid, GridItem, Title } from '@patternfly/react-core';
import { type HealthVerdict, productHealth } from '../../../../../shared/riskregister/productReport';
import type { Payload, Row, ViewState } from '../../../../../shared/riskregister/types';
import '../../productReport.css';

interface SummaryTile {
  title: string;
  value: number;
  subtitle: string;
  tone: 'danger' | 'warning' | 'info' | 'success' | 'neutral';
}

const VERDICT_LABEL: Record<HealthVerdict, string> = {
  healthy: 'Going well',
  watch: 'Keep an eye on it',
  'at-risk': 'Needs attention',
};

const SummaryTile: React.FunctionComponent<SummaryTile> = ({ title, value, subtitle, tone }) => (
  <Card isCompact className={`risk-register__metric-card risk-register__metric-card--${tone}`}>
    <CardBody>
      <Title headingLevel="h3" size="md">
        {title}
      </Title>
      <strong aria-label={`${title}: ${value}`}>{value}</strong>
      <p>{subtitle}</p>
    </CardBody>
  </Card>
);

export const ProductSummary: React.FunctionComponent<{
  payload: Payload;
  rows: Row[];
  state: ViewState;
}> = ({ payload, rows, state }) => {
  const health = React.useMemo(() => productHealth(rows, payload), [payload, rows]);
  const scope = state.area === 'ALL' ? 'all platform areas' : state.area;

  if (!rows.length) {
    return <p className="risk-register__empty">There is no work to report on for {scope} right now.</p>;
  }

  const tiles: SummaryTile[] = [
    {
      title: 'Open work',
      value: health.open,
      tone: 'info',
      subtitle: `Everything still in flight across ${scope}. This is the total the other numbers come out of.`,
    },
    {
      title: 'Late',
      value: health.late,
      tone: 'danger',
      subtitle: health.late
        ? 'Already past the date they were promised for. Ask about these first.'
        : 'Nothing has gone past the date it was promised for.',
    },
    {
      title: 'Waiting on someone',
      value: health.waiting,
      tone: 'warning',
      subtitle: `Nobody has moved these for ${health.waitingDays} days or more, so they are stuck rather than slow.`,
    },
    {
      title: 'Highest severity',
      value: health.severe,
      tone: 'danger',
      subtitle:
        'Rated most serious in the risk review, a score of how much damage the problem could do if it is left alone.',
    },
    {
      title: 'Finished recently',
      value: health.finished,
      tone: 'success',
      subtitle: `Completed in the last ${health.recentDays} days, so this is the pace work is actually coming off the list.`,
    },
  ];

  return (
    <>
      <div className={`risk-register__product-intro risk-register__product-intro--${health.verdict}`}>
        <span className="risk-register__product-verdict">{VERDICT_LABEL[health.verdict]}</span>
        <p>
          Across {scope}, {health.open} piece{health.open === 1 ? '' : 's'} of work {health.open === 1 ? 'is' : 'are'}{' '}
          still open as of {payload.as_of}. {health.reason}
        </p>
        <p>
          {health.attention} of them need someone to look at them, because they are late, stuck waiting on someone, or
          rated most serious. {health.finished} finished in the last {health.recentDays} days.
        </p>
      </div>
      <Grid hasGutter className="risk-register__metric-grid">
        {tiles.map((tile) => (
          <GridItem key={tile.title} span={12} sm={6} lg={3}>
            <SummaryTile {...tile} />
          </GridItem>
        ))}
      </Grid>
    </>
  );
};
